import React, { useState, useEffect } from 'react';
import { X, CheckCircle, ArrowRight, Calculator, FileText, Zap } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUIZ_QUESTIONS = [
  {
    q: "What's your monthly revenue range?",
    options: [
      { text: "Upto 50 Lacs", score: { starter: 3, growth: 1, enterprise: 0 } },
      { text: "50 Lacs to 1 Crore", score: { starter: 1, growth: 3, enterprise: 1 } },
      { text: "1 to 10 Crores", score: { starter: 0, growth: 2, enterprise: 3 } },
      { text: "10 Crores and above", score: { starter: 0, growth: 1, enterprise: 3 } }
    ]
  },
  {
    q: "How many employees do you have?",
    options: [
      { text: "1-10", score: { starter: 3, growth: 1, enterprise: 0 } },
      { text: "11-50", score: { starter: 1, growth: 3, enterprise: 1 } },
      { text: "50+", score: { starter: 0, growth: 1, enterprise: 3 } }
    ]
  },
  {
    q: "What's your biggest finance pain point?",
    options: [
      { text: "GST/TDS filing is always late", score: { starter: 3, growth: 2, enterprise: 1 } },
      { text: "No visibility into cash flow", score: { starter: 1, growth: 3, enterprise: 2 } },
      { text: "Need investor-ready financials", score: { starter: 0, growth: 1, enterprise: 3 } },
      { text: "Managing multiple entities", score: { starter: 0, growth: 1, enterprise: 3 } }
    ]
  }
];

const PLAN_RESULTS = {
  starter: { name: "Starter", price: "1,00,000", saving: "4L+/year vs in-house", color: "emerald" },
  growth: { name: "Growth", price: "1,50,000", saving: "8L+/year vs in-house", color: "teal" },
  enterprise: { name: "Enterprise", price: "2,50,000", saving: "15L+/year vs in-house", color: "slate" }
};

export const ExitQuiz = ({ onBookAppointment }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ starter: 0, growth: 0, enterprise: 0 });
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    const handleExit = (e) => {
      if (e.clientY <= 5 && !show && !dismissed) {
        setShow(true);
      }
    };
    document.addEventListener('mouseleave', handleExit);
    return () => document.removeEventListener('mouseleave', handleExit);
  }, [show, dismissed]);

  const handleAnswer = (option) => {
    const newScores = { ...scores };
    Object.keys(option.score).forEach(k => { newScores[k] += option.score[k]; });
    setScores(newScores);

    if (step < QUIZ_QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      const best = Object.entries(newScores).sort((a, b) => b[1] - a[1])[0][0];
      setResult(PLAN_RESULTS[best]);
    }
  };

  const handleUnlock = async () => {
    if (!email) return;
    setShowResult(true);
    try {
      await fetch(`${BACKEND_URL}/api/tracking/capture-lead`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
    } catch (e) { console.error('Lead capture failed:', e); }
  };

  const dismiss = () => { setShow(false); setDismissed(true); };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={dismiss}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="exit-quiz">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white relative">
          <button onClick={dismiss} className="absolute top-3 right-3 text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
          <Calculator className="w-8 h-8 mb-2" />
          <h3 className="text-xl font-bold">Which Plan Saves You the Most?</h3>
          <p className="text-emerald-100 text-sm mt-1">30-second quiz — get your personalized recommendation</p>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              <div className="flex gap-1 mb-4">
                {QUIZ_QUESTIONS.map((_, i) => (
                  <div key={`progress-${i}`} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-gray-200'}`}></div>
                ))}
              </div>
              <p className="font-semibold text-gray-900 mb-4">{QUIZ_QUESTIONS[step].q}</p>
              <div className="space-y-2">
                {QUIZ_QUESTIONS[step].options.map((opt, i) => (
                  <button key={opt || `opt-${i}`} onClick={() => handleAnswer(opt)} data-testid={`quiz-option-${i}`}
                    className="w-full text-left px-4 py-3 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-sm font-medium">
                    {opt.text}
                  </button>
                ))}
              </div>
            </>
          ) : !showResult ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="font-bold text-lg text-gray-900 mb-2">Your result is ready!</h4>
              <p className="text-gray-600 text-sm mb-4">Enter your email to see your personalized plan recommendation and potential savings.</p>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                className="w-full border-2 rounded-xl px-4 py-3 mb-3 focus:border-emerald-500 focus:outline-none" data-testid="quiz-email" />
              <Button onClick={handleUnlock} disabled={!email} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 rounded-xl" data-testid="quiz-unlock">
                See My Result <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-emerald-700 font-medium">Recommended for you</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{result.name} Plan</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">{'\u20B9'}{result.price}<span className="text-sm text-gray-500">/month</span></p>
                <p className="text-sm text-emerald-700 mt-2 font-medium">You save {result.saving}</p>
              </div>
              <Button onClick={() => { dismiss(); if (onBookAppointment) onBookAppointment(); }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 rounded-xl" data-testid="quiz-book-btn">
                Book Free Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Social proof notification
export const SocialProofNotification = () => {
  const [show, setShow] = useState(false);
  const [notification, setNotification] = useState(null);

  const cities = ["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Pune", "Chennai", "Ahmedabad", "Kolkata", "Jaipur", "Lucknow"];
  const actions = [
    "just booked a free consultation",
    "signed up for the Starter plan",
    "downloaded the GST compliance checklist",
    "booked a strategy session"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      const city = cities[Math.floor(Math.random() * cities.length)];
      const action = actions[Math.floor(Math.random() * actions.length)];
      const mins = Math.floor(Math.random() * 15) + 1;
      setNotification({ city, action, mins });
      setShow(true);
      setTimeout(() => setShow(false), 4000);
    }, 25000 + Math.random() * 20000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show || !notification) return null;

  return (
    <div className="fixed bottom-24 left-6 z-40 animate-in slide-in-from-left duration-500" data-testid="social-proof">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-3 flex items-center gap-3 max-w-xs">
        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">Someone in {notification.city}</p>
          <p className="text-xs text-gray-500">{notification.action} — {notification.mins}m ago</p>
        </div>
      </div>
    </div>
  );
};

// Blog CTA bar
export const BlogCtaBar = ({ onOpenChat }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await fetch(`${BACKEND_URL}/api/tracking/download-resource`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, resource_type: 'gst-checklist' })
      });
      setSubmitted(true);
    } catch (e) { setSubmitted(true); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 my-8 text-white" data-testid="blog-cta-bar">
      {!submitted ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5" />
            <h3 className="font-bold">Free Financial Health Check</h3>
          </div>
          <p className="text-emerald-100 text-sm mb-4">Get a personalized assessment of your business finances. Enter your email and our CA team will reach out.</p>
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
              className="flex-1 rounded-lg px-4 py-2 text-gray-900 text-sm focus:outline-none" data-testid="blog-cta-email" />
            <Button type="submit" className="bg-white text-emerald-700 hover:bg-emerald-50 px-6 font-semibold" data-testid="blog-cta-submit">
              Get Free Check
            </Button>
          </form>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <CheckCircle className="w-6 h-6" />
          <p className="font-medium">We'll email you your free financial health check within 24 hours!</p>
        </div>
      )}
      {onOpenChat && (
        <button onClick={onOpenChat} className="mt-3 text-emerald-200 hover:text-white text-sm underline transition-colors">
          Or chat with Priya now for instant advice →
        </button>
      )}
    </div>
  );
};

// Gated resource download
export const GatedResource = ({ title, description, resourceType, onBookAppointment }) => {
  const [email, setEmail] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [sending, setSending] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tracking/download-resource`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, resource_type: resourceType })
      });
      if (res.ok) {
        setUnlocked(true);
      }
    } catch (e) { setUnlocked(true); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-6 text-center" data-testid={`gated-resource-${resourceType}`}>
      <FileText className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
      <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      {!unlocked ? (
        <form onSubmit={handleUnlock} className="space-y-2">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email to download"
            className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-emerald-500" />
          <Button type="submit" disabled={!email || sending} className="w-full bg-emerald-600 text-white">
            {sending ? 'Sending...' : 'Download Free'}
          </Button>
        </form>
      ) : (
        <div>
          <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm text-emerald-700 font-medium">Check your email! We've sent the resource.</p>
          <button onClick={onBookAppointment} className="mt-3 text-sm text-emerald-600 underline hover:text-emerald-700">
            Also book a free consultation →
          </button>
        </div>
      )}
    </div>
  );
};
